import { BuildingIcon } from 'components/icons';
import { RecordChip } from 'components/record-chip/RecordChip';
import { SearchTermHighlight } from 'components/SearchTermHighlight';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { Link as RouterLink } from 'react-router-dom';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { PartyType } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { lastNameFirstName, useLastNameFirst } from 'utils/nameOrder';
import {
    deceasedAdornment,
    deletedAdornment,
    emailAdornment,
    PartyNameParty,
    partyNumberString,
    partyTypeIconAdornment,
} from 'utils/partyNameUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import PersonaNonGrataChip from './PersonaNonGrataChip';

/**
 * Controls the faded color applied to the name when the contact is deceased:
 * - `list` — global contacts list (spec rule 1) → faded grey.
 * - `readonly-self` — the contact's own read-only view (spec rule 2) → faded primary green.
 * - `readonly-other` — any other read-only view referencing the contact (spec rule 3) → faded info blue.
 * - `form` — edit forms (spec rule 4) → regular color.
 *
 * Non-deceased contacts render in regular color regardless of variant.
 */
export type PartyNameVariant = 'list' | 'readonly-self' | 'readonly-other' | 'form';

interface PartyNameProps {
    party: PartyNameParty;
    /** A link to the contact: in the new design its record chip, the suffixes, mark and e-mail after it. */
    isLink?: boolean;
    /**
     * Keeps the plain link in the new design too: for the name cell of the contact's own list row, which names the row
     * rather than a record the row points to.
     */
    plainLink?: boolean;
    /** Use `LinkWithOrigin` (records `returnTo`) instead of plain `RouterLink`. */
    useLinkWithOrigin?: boolean;
    linkState?: unknown;
    linkTarget?: string;
    variant?: PartyNameVariant;
    useOfficialName?: boolean;

    renderPartyIcon?: boolean;
    showPartyNumber?: boolean;
    showEmail?: boolean;
    showDeceasedSuffix?: boolean;
    showDeletedSuffix?: boolean;
    showPersonaNonGrataIcon?: boolean;
    /**
     * Pixel size of the Persona non grata icon glyph (chip scales to 1.5× this value). Useful in
     * larger headings. Defaults to 16. Only affects the PNG chip — the party-type icon is not
     * resized.
     */
    personaNonGrataIconSize?: number;
    /**
     * Render the name on ONE line, ellipsized, instead of wrapping it.
     *
     * For a fixed-width host — a managed table cell — where wrapping makes the row taller than its
     * neighbours. This has to be a prop rather than something the host imposes: the name text is a
     * flex item several levels down, so a clamp or an `overflow` on the outside cannot reach it, and
     * a flex chain produces no line boxes for `-webkit-line-clamp` to count.
     */
    singleLine?: boolean;
    /** The new design's chip draws no native title, for a host that reveals a cut name itself (`EllipsisTooltip`). */
    hideTitle?: boolean;
    /** The party number after the name, in the muted grey, instead of before it. A record chip keeps it first. */
    numberAfter?: boolean;
    /**
     * The new design's muted second line of a list row, such as the client under a case: a building glyph
     * before the name, 13px secondary text, and the number after the name as `numberAfter` puts it. As a link it is
     * the contact's record chip in that text size, whose glyph stands for the building's.
     */
    secondaryLine?: boolean;

    className?: string;
}

/** The deceased colour per variant; `!` on the link so it beats the link's own colour. */
const DECEASED_CLASS: Record<PartyNameVariant, string | undefined> = {
    list: 'text-grey-700/50 [&_a]:text-grey-700/50!',
    'readonly-self': 'text-primary/50 [&_a]:text-primary/50!',
    'readonly-other': 'text-info-darker/50 [&_a]:text-info-darker/50!',
    form: undefined,
};

function shouldShowDeceased(party: PartyNameParty): boolean {
    return !!(party.flags?.deceased && party.partyType === PartyType.PERSON);
}

export default function PartyName({
    party,
    isLink = false,
    plainLink = false,
    useLinkWithOrigin = false,
    linkState,
    linkTarget,
    variant = 'form',
    useOfficialName = false,
    renderPartyIcon = false,
    showPartyNumber = false,
    showEmail = false,
    showDeceasedSuffix = true,
    showDeletedSuffix = true,
    showPersonaNonGrataIcon = true,
    personaNonGrataIconSize,
    singleLine = false,
    hideTitle = false,
    numberAfter = false,
    secondaryLine = false,
    className,
}: PartyNameProps) {
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const lastNameFirst = useLastNameFirst();
    const { data: currentTenant } = useGetCurrentTenantQuery();
    const showsNumberWithName = currentTenant?.showPartyNumberWithName !== false;
    // The new design's record chip, for a link to a contact that has an id.
    const recordChipPartyId = useIsNewDesign() && isLink && !plainLink ? party.id : undefined;

    const flippedName = lastNameFirst ? lastNameFirstName(party) : null;
    const baseName = flippedName ?? (useOfficialName ? party.officialName || party.name : party.name);
    const partyNumberLabel =
        showPartyNumber && showsNumberWithName ? partyNumberString(party, isPartyNumberUsageEnabled) : null;
    const numberLast = numberAfter || secondaryLine;
    // Its `[&_a]` recolour would reach a chip, whose colours are its family's.
    const deceasedClass =
        showDeceasedSuffix && shouldShowDeceased(party) && !recordChipPartyId ? DECEASED_CLASS[variant] : undefined;

    const deceased = showDeceasedSuffix ? deceasedAdornment(party) : null;
    const deleted = showDeletedSuffix ? deletedAdornment(party) : null;
    const isPersonaNonGrata = showPersonaNonGrataIcon && !!party.isPersonaNonGrata;
    const personaNonGrataChip = isPersonaNonGrata ? <PersonaNonGrataChip iconSize={personaNonGrataIconSize} /> : null;

    // Wrap the name text in its own span so it forms a single flex item that can wrap internally —
    // or, under `singleLine`, one that shrinks and ellipsizes instead. The `min-w-0` is what lets it
    // shrink at all: a flex item's automatic minimum size is its content.
    const nameText = (
        <span className={singleLine ? 'min-w-0 truncate' : undefined}>
            {partyNumberLabel && !numberLast && <>{partyNumberLabel} </>}
            <SearchTermHighlight text={baseName} />
            {deceased && <> {deceased}</>}
            {deleted && <> {deleted}</>}
            {partyNumberLabel && numberLast && (
                <>
                    {' '}
                    <span className="text-muted-foreground">{partyNumberLabel}</span>
                </>
            )}
        </span>
    );

    // The chip sits alongside the name as a flex sibling — inside the Link when `isLink`, so that
    // hovering the chip underlines the link and clicking it navigates. `display: inline-flex` on the
    // chip wrapper prevents the link's underline from propagating onto the chip itself. Beside a
    // record chip it stays outside, as every signal around a name does.
    const chipElement = personaNonGrataChip && <span className="ml-0.5 inline-flex">{personaNonGrataChip}</span>;

    const linkProps = {
        to: `/contact/${party.id}`,
        state: linkState,
        target: linkTarget,
        rel: linkTarget === '_blank' ? 'noopener noreferrer' : undefined,
        className: cn(
            // `entity-link` is a targeting hook: the table-cell and detail-row link rules colour links by it.
            'entity-link text-fg hover:text-link inline-flex items-center gap-x-1 no-underline hover:underline',
            secondaryLine && 'text-foreground-secondary',
            singleLine ? 'max-w-full min-w-0 flex-nowrap' : 'flex-wrap'
        ),
    };
    const linkedName = recordChipPartyId ? (
        <>
            <RecordChip
                entityType="PARTY"
                id={recordChipPartyId}
                label={baseName}
                partyNumber={partyNumberLabel ? party.partyNumber : null}
                withOrigin={useLinkWithOrigin}
                state={linkProps.state}
                target={linkProps.target}
                rel={linkProps.rel}
                hideTitle={hideTitle}
                hitArea={false}
            />
            {deceased}
            {deleted}
            {chipElement}
        </>
    ) : isLink && party.id ? (
        useLinkWithOrigin ? (
            <LinkWithOrigin {...linkProps}>
                {nameText}
                {chipElement}
            </LinkWithOrigin>
        ) : (
            <RouterLink {...linkProps}>
                {nameText}
                {chipElement}
            </RouterLink>
        )
    ) : (
        <>
            {nameText}
            {chipElement}
        </>
    );

    // The chip's glyph stands for either icon.
    const icon = recordChipPartyId ? null : secondaryLine ? (
        <BuildingIcon className="text-muted-foreground size-[13px]" />
    ) : renderPartyIcon ? (
        partyTypeIconAdornment(party)
    ) : null;
    const email = showEmail ? emailAdornment(party) : null;

    return (
        <span
            className={cn(
                // Inline-flex so the chip (fixed-height flex item) stays vertically centered with the
                // name text. `flex-wrap` preserves the wrapping of the name text across multiple lines.
                'inline-flex items-center gap-x-1',
                // A one-line name still has to give way to its host's width, and the chip beside it
                // must keep its size — it is a flag, not decoration.
                singleLine ? 'max-w-full min-w-0 flex-nowrap [&>*:not(:first-child)]:shrink-0' : 'flex-wrap',
                secondaryLine && 'text-foreground-secondary text-meta',
                deceasedClass,
                className
            )}
        >
            {icon && <span className="inline-flex">{icon}</span>}
            {linkedName}
            {email && <span>{email}</span>}
        </span>
    );
}
